// Debug component to test vendor addon groups API
import React, { useState } from 'react';
import api from '../utils/axios';

const AddonGroupsDebug = () => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});

  const testEndpoint = async (name, url, method = 'GET', data = null) => {
    setLoading(prev => ({ ...prev, [name]: true }));
    
    try {
      let response;
      if (method === 'GET') {
        response = await api.get(url);
      } else if (method === 'POST') {
        response = await api.post(url, data);
      } else if (method === 'DELETE') {
        response = await api.delete(url);
      }
      
      setResults(prev => ({
        ...prev,
        [name]: {
          success: true,
          status: response.status,
          data: response.data,
          timestamp: new Date().toLocaleTimeString()
        }
      }));
      
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [name]: {
          success: false,
          status: error.response?.status || 'Network Error',
          error: error.response?.data?.message || error.message,
          timestamp: new Date().toLocaleTimeString()
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [name]: false }));
    }
  };

  const testAddonMapping = async () => {
    setLoading(prev => ({ ...prev, 'test-mapping': true }));
    
    try {
      // First get available addon groups
      const groupsResponse = await api.get('/api/addon-groups');
      const groups = groupsResponse.data.addonGroups || [];
      
      // Get menu items
      const itemsResponse = await api.get('/vendor/menu-items');
      const items = itemsResponse.data || [];
      
      if (groups.length === 0 || items.length === 0) {
        throw new Error(`Need both groups (${groups.length}) and items (${items.length}) to test mapping`);
      }
      
      const firstGroup = groups[0];
      const firstItem = items[0];
      
      // Try to map addon group to menu item
      const mapResponse = await api.post(`/api/menu-items/${firstItem.id}/addon-groups/${firstGroup.id}`, {
        sort_order: 1
      });
      
      setResults(prev => ({
        ...prev,
        'test-mapping': {
          success: true,
          status: mapResponse.status,
          data: {
            message: `Successfully mapped "${firstGroup.name}" to "${firstItem.name}"`,
            response: mapResponse.data
          },
          timestamp: new Date().toLocaleTimeString()
        }
      }));
      
    } catch (error) {
      setResults(prev => ({
        ...prev,
        'test-mapping': {
          success: false,
          status: error.response?.status || 'Network Error',
          error: error.response?.data?.message || error.message,
          timestamp: new Date().toLocaleTimeString()
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, 'test-mapping': false }));
    }
  };

  const TestButton = ({ name, url, method, data, label }) => (
    <button
      onClick={() => testEndpoint(name, url, method, data)}
      disabled={loading[name]}
      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
    >
      {loading[name] ? 'Testing...' : label}
    </button>
  );

  const ResultDisplay = ({ name }) => {
    const result = results[name];
    if (!result) return null;

    return (
      <div className={`mt-2 p-3 rounded ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex justify-between items-center mb-2">
          <span className={`font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
            {result.success ? '✅ Success' : '❌ Failed'}
          </span>
          <span className="text-sm text-gray-500">{result.timestamp}</span>
        </div>
        <div className="text-sm">
          <strong>Status:</strong> {result.status}
        </div>
        {result.success ? (
          <div className="text-sm mt-2">
            <strong>Response:</strong>
            <pre className="mt-1 text-xs bg-white p-2 rounded overflow-auto max-h-40">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        ) : (
          <div className="text-sm mt-2 text-red-700">
            <strong>Error:</strong> {result.error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">🧪 Vendor Addon Groups API Tester</h2>
      
      <div className="space-y-6">
        {/* Authentication Check */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">🔐 Authentication</h3>
          <div className="mb-2">
            <strong>Token Status:</strong> {localStorage.getItem('vendorToken') ? '✅ Present' : '❌ Missing'}
          </div>
          {localStorage.getItem('vendorToken') && (
            <div className="text-xs text-gray-600">
              <strong>Token:</strong> {localStorage.getItem('vendorToken').substring(0, 20)}...
            </div>
          )}
        </div>

        {/* API Tests */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">🧩 Addon Groups API Tests</h3>
          
          <div className="space-y-4">
            {/* Test 1: Basic Health Check */}
            <div>
              <TestButton
                name="ping"
                url="/api/ping"
                label="Test Server Connection"
              />
              <ResultDisplay name="ping" />
            </div>

            {/* Test 2: Public Addon Groups */}
            <div>
              <TestButton
                name="public-groups"
                url="/api/addon-groups"
                label="Get Public Addon Groups"
              />
              <ResultDisplay name="public-groups" />
            </div>

            {/* Test 3: Vendor Addon Groups (Enhanced) */}
            <div>
              <TestButton
                name="vendor-groups"
                url="/api/vendor/addon-groups"
                label="Get Vendor Addon Groups (Enhanced)"
              />
              <ResultDisplay name="vendor-groups" />
            </div>

            {/* Test 4: Menu Items for Mapping */}
            <div>
              <TestButton
                name="menu-items-mapping"
                url="/api/vendor/menu-items-for-mapping"
                label="Get Menu Items for Mapping"
              />
              <ResultDisplay name="menu-items-mapping" />
            </div>

            {/* Test 5: Get Menu Items */}
            <div>
              <TestButton
                name="menu-items"
                url="/vendor/menu-items"
                label="Get Menu Items (Vendor)"
              />
              <ResultDisplay name="menu-items" />
            </div>

            {/* Test 6: Get Item Addon Groups (requires menu item ID) */}
            <div>
              <TestButton
                name="item-addon-groups"
                url="/api/menu-items/1/addon-groups"
                label="Get Item #1 Addon Groups"
              />
              <ResultDisplay name="item-addon-groups" />
            </div>

            {/* Test 7: Test Addon Group Mapping */}
            <div>
              <button
                onClick={testAddonMapping}
                disabled={loading['test-mapping']}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
              >
                {loading['test-mapping'] ? 'Testing...' : '🔄 Test Addon Group Mapping'}
              </button>
              <ResultDisplay name="test-mapping" />
            </div>

            {/* Test 8: Add Sample Toppings */}
            <div>
              <button
                onClick={async () => {
                  setLoading(prev => ({ ...prev, 'add-toppings': true }));
                  
                  try {
                    // Get Extra Toppings group
                    const groupsResponse = await api.get('/api/addon-groups');
                    const groups = groupsResponse.data.addonGroups || [];
                    const extraToppingsGroup = groups.find(g => g.name.toLowerCase().includes('extra') || g.name.toLowerCase().includes('topping'));
                    
                    if (!extraToppingsGroup) {
                      throw new Error('Extra Toppings group not found');
                    }
                    
                    // Sample toppings to add
                    const toppings = [
                      { name: 'Nuts', price: 1.50, description: 'Roasted mixed nuts' },
                      { name: 'Chocolate Chips', price: 2.00, description: 'Dark chocolate chips' },
                      { name: 'Sprinkles', price: 0.75, description: 'Colorful sprinkles' }
                    ];
                    
                    const results = [];
                    for (let i = 0; i < toppings.length; i++) {
                      const topping = toppings[i];
                      const response = await api.post(`/api/vendor/addon-groups/${extraToppingsGroup.id}/addons`, {
                        name: topping.name,
                        description: topping.description,
                        price: topping.price,
                        max_quantity: 3,
                        min_quantity: 0,
                        is_available: true,
                        sort_order: i + 1,
                        default: false
                      });
                      results.push(response.data);
                    }
                    
                    setResults(prev => ({
                      ...prev,
                      'add-toppings': {
                        success: true,
                        status: 201,
                        data: {
                          message: `Added ${toppings.length} toppings to "${extraToppingsGroup.name}"`,
                          toppings: results
                        },
                        timestamp: new Date().toLocaleTimeString()
                      }
                    }));
                    
                  } catch (error) {
                    setResults(prev => ({
                      ...prev,
                      'add-toppings': {
                        success: false,
                        status: error.response?.status || 'Error',
                        error: error.response?.data?.message || error.message,
                        timestamp: new Date().toLocaleTimeString()
                      }
                    }));
                  } finally {
                    setLoading(prev => ({ ...prev, 'add-toppings': false }));
                  }
                }}
                disabled={loading['add-toppings']}
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
              >
                {loading['add-toppings'] ? 'Adding...' : '🍫 Add Sample Toppings (Nuts, Chocolate, Sprinkles)'}
              </button>
              <ResultDisplay name="add-toppings" />
            </div>

            {/* Test 9: Create Test Group */}
            <div>
              <TestButton
                name="create-group"
                url="/api/addon-groups"
                method="POST"
                data={{
                  name: "Test Group",
                  description: "Test description",
                  is_required: false,
                  min_quantity: 0,
                  max_quantity: 5
                }}
                label="Create Test Addon Group"
              />
              <ResultDisplay name="create-group" />
            </div>
          </div>
        </div>

        {/* Network Info */}
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">🌐 Network Info</h3>
          <div className="text-sm space-y-1">
            <div><strong>Base URL:</strong> {api.defaults.baseURL}</div>
            <div><strong>Current URL:</strong> {window.location.href}</div>
            <div><strong>Expected:</strong> http://localhost:3000 (Local Development)</div>
            <div><strong>User Agent:</strong> {navigator.userAgent}</div>
          </div>
        </div>

        {/* Instructions */}
        <div className="border rounded-lg p-4 bg-blue-50">
          <h3 className="text-lg font-semibold mb-3">📋 Troubleshooting Guide</h3>
          <div className="text-sm space-y-2">
            <div><strong>1. Server Connection:</strong> Should return "pong"</div>
            <div><strong>2. Public Groups:</strong> Should return array of addon groups</div>
            <div><strong>3. Vendor Groups:</strong> Should return enhanced vendor data with usage stats</div>
            <div><strong>4. If 401 Error:</strong> Check if you're logged in as vendor</div>
            <div><strong>5. If 404 Error:</strong> Check if server routes are properly loaded</div>
            <div><strong>6. If Network Error:</strong> Check if backend server is running</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddonGroupsDebug;
